import React, { useEffect, useState } from 'react';
import MetricsTable from '@/pages/intelligence/pages/TopCharts/PCGames/MetricsTable';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { npdList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import moment from 'moment';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import { getDeviceValuesByPageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import {
  commonPCConsoleDefaultFilterValue,
  handleMetricTableFilterParamsToApi,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { defaultMetrictableApiParams } from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils/constant';
import { useIntl, history } from 'umi';
import { getTopPCMetricTableCustomValidUrlParams, topchartConsolePhysicalRevenueValidCfg, hasShortLinkRatio } from '@intelligence/_config/urlPageValidCfg';
import { getOriginalShortLinkFilter, URL_FILTER_KEY, URL_CHART_METRIC, URL_CHART_TYPE } from '@intelligence/utils/urlPathCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

export default function PhysicalRevenue() {
  const { formatMessage } = useIntl(); // 中英切换方法
  const pageId = TopchartPageId.PhysicalRevenue;
  const entityType = 'console';

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.consolePhysicalRevenue?.type,
  });

  const filterLoading = false;
  const regionOptions: any[] = [];

  const defaultFilterValue = {
    ...commonPCConsoleDefaultFilterValue,
    [filterKeyMap.PHYSICAL_DEVICE]: {
      value: getDeviceValuesByPageId(pageId),
    },
  };

  const hasUrlShortLink = hasShortLinkRatio.topConsolePhysicalRevenue;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: topchartConsolePhysicalRevenueValidCfg?.[URL_CHART_TYPE]?.[0],
    [URL_CHART_METRIC]: topchartConsolePhysicalRevenueValidCfg?.[URL_CHART_METRIC]?.[0],
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopPCMetricTableCustomValidUrlParams(history, topchartConsolePhysicalRevenueValidCfg, npdList);
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(dbiPageLogMes.topChartsConsoleGamesPhysicalRevenue);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const filterCfg: TopchartFilterCfgType = {
    filterKeyList: [
      filterKeyMap.MY_FILTER,
      filterKeyMap.RANGE_TIME,
      filterKeyMap.GENRETOR,
      filterKeyMap.ALL_Games,
      filterKeyMap.PHYSICAL_DEVICE,
      filterKeyMap.ALL_COMPANYS,
      filterKeyMap.RELEASE_TIME,
    ],
    companyFilterParams: {
      entity_type: entityType,
      region_type: 'market',
      regions: ['us'],
      granularity: 'monthly',
      start_date: '',
      end_date: '',
      search_name: '',
      page: 1,
      page_size: 500,
      source: 'npd',
      metric: 'revenue',
      data_sources: ['npd'],
      source_metric: {
        npd: ['revenue'],
      },
    },
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: [moment().subtract(6, 'months'), moment().subtract(1, 'months')],
      },
    },
    specialParams: {
      regionOptions, // 国家筛选器options,
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      rangeTimeType: FilterTimeType.rangeMonth,
      disableIegg: false,
      disableAnnie: true,
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      entityType
    },
    iconText: (
      <div>
        <p>{formatMessage({ id: 'dbi_releasedatefilter_info' })}</p>
      </div>
    ),
    buttonText: formatMessage({ id: 'dbi_us_only' }),
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    handleFilterToApiParams: (params: any) => handleMetricTableFilterParamsToApi(params, entityType),
    hasUrlShortLink,
  };

  const defaultParams = {
    ...defaultMetrictableApiParams,
    entity_type: entityType,
    order_metric: 'revenue',
    order_source: 'npd',
    source_metrics: {
      npd: ['revenue', 'physical_units'],
    },
    topchart_name: 'Physical Revenue',
    region_type: 'market',
    regions: ['global'],
    date_type: 'monthly',
    device: getDeviceValuesByPageId(pageId),
  };

  return (
    <>
      <MarqueeBanner />
      <MetricsTable
        entityType={entityType}
      pageId={pageId}
      pageLogInfo={dbiPageLogMes.topChartsConsoleGamesPhysicalRevenue}
      downloadModalSize={844}
      visualLoadingHeight={402}
      tableCloumsList={npdList}
      hasRegionFilter={false}
      cardMetricName={formatMessage({ id: 'dbi_physical_revenue' })}
      filterCfg={filterCfg}
      filterLoading={filterLoading || shortLinkLoading}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      defaultParams={defaultParams}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
    />
    </>
  );
}
